import React, {
 useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

import ChipToggle from './ChipToggle';
import PostsFilter from './PostsFilter';

// Role filter is media-only — promoters only ever receive from media, so
// filtering their conversations by sender role doesn't surface anything useful.
// Media users get a "Media only" option for group threads with other media.
const ROLE_OPTIONS = [
  { value: 'all', label: 'All roles' },
  { value: 'experts', label: 'Experts only' },
  { value: 'prs', label: 'PRs only' },
  { value: 'media', label: 'Media only' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Show all' },
  { value: 'unread', label: 'Unread only' },
  { value: 'read', label: 'Read only' },
];

function RadioFilterChip({
 label, title, value, options, onChange,
}) {
  const [pendingValue, setPendingValue] = useState(value || options[0].value);
  const [show, setShow] = useState(false);

  const handleToggle = (isOpen) => {
    setShow(isOpen);
    if (isOpen) {
      setPendingValue(value || options[0].value);
    }
  };

  const handleApply = () => {
    onChange(pendingValue === options[0].value ? null : pendingValue);
    setShow(false);
  };

  const hasChanged = pendingValue !== (value || options[0].value);

  return (
    <Dropdown show={show} onToggle={handleToggle}>
      <Dropdown.Toggle as={ChipToggle} filterActive={value != null}>
        {label}
        <i className="fa-solid fa-chevron-down paragraph-sm fw-normal" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="px-3 py-3">
        <p className="paragraph-md fw-bold mb-3">{title}</p>

        {options.map((option) => (
          <div key={option.value} className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              name={`filter-${label}`}
              id={`filter-${label}-${option.value}`}
              checked={pendingValue === option.value}
              onChange={() => setPendingValue(option.value)}
              style={{ cursor: 'pointer' }}
            />
            <label
              className="form-check-label paragraph-md"
              htmlFor={`filter-${label}-${option.value}`}
              style={{ cursor: 'pointer' }}
            >
              {option.label}
            </label>
          </div>
        ))}

        <button type="button" className="btn btn-primary w-100 mt-2 py-1" disabled={!hasChanged} onClick={handleApply}>
          Apply
        </button>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function ConversationsFilter({
 activeFilters, filtersVisible, onToggleFilters, onFilterChange, isMedia = true,
}) {
  const updateFilter = (key, value) => {
    onFilterChange({ ...activeFilters, [key]: value });
  };

  // Options are fetched LAZILY, on first open of the Posts dropdown — not on mount. This
  // control is rendered on every inbox visit but touched by few, and the fetch is the
  // expensive part (some profiles page through thousands of rows). Keyed by request id so a
  // request appears ONCE however its conversations spell the subject ("Re: <truncated>").
  const [postOptionRows, setPostOptionRows] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const fetchedRef = useRef(false);
  // The fetch lives in a callback now, not an effect, so it has no cleanup of its own —
  // this stands in for the old `cancelled` flag and keeps setState off an unmounted tree.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );
  const currentReceiverProfileId = globalThis.Qwoted?.CURRENT_PROFILE_ID;

  // Re-arm when the viewer changes identity; the cached rows belong to the old one.
  useEffect(() => {
    fetchedRef.current = false;
    setPostOptionRows([]);
  }, [currentReceiverProfileId, isMedia]);

  const loadPostOptions = useCallback(async () => {
    if (fetchedRef.current || !currentReceiverProfileId) return;
    fetchedRef.current = true; // guard first: reopening must not refetch
    setOptionsLoading(true);
    // Media own requests (one row each); promoters can only be reached through their pitches.
    const { data, error } = isMedia
      ? await qapi.requests.listOwnedWithPitches(currentReceiverProfileId)
      : await qapi.pitches.listPitchedRequestOptions(currentReceiverProfileId);
    if (error) {
      qlog.error('Failed to fetch requests for the Posts filter:', error);
      fetchedRef.current = false; // let a retry happen on next open
    }
    if (!mountedRef.current) return;
    const seen = new Set();
    const rows = [];
    (data || []).forEach((row) => {
      const id = row.id ?? row.request_id;
      const title = row.title ?? row.request_title;
      if (id && title && !seen.has(id)) {
        seen.add(id);
        rows.push({ id, title });
      }
    });
    setPostOptionRows(rows);
    setOptionsLoading(false);
  }, [currentReceiverProfileId, isMedia]);

  // Full titles, truncated with CSS rather than sliced here: a hard slice put an ellipsis in
  // the DOM and left nowhere to read the rest, so hovering showed the same clipped string.
  // Clipping in CSS also adapts to the menu width instead of a fixed 40 characters.
  const postOptions = useMemo(
    () => postOptionRows
        .map(({ id, title }) => ({ value: id, label: title }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [postOptionRows],
  );

  const clearAllFilters = () => {
    onFilterChange({ role: null, status: null, posts: null });
  };

  // Role filter is hidden for promoters; if a stale role filter is in state when
  // a non-media user lands here, treat it as inactive so the tag pill doesn't render.
  const activeRole = isMedia ? activeFilters.role : null;
  const hasActiveFilters = activeRole || activeFilters.status || activeFilters.posts;

  return (
    <>
      <button
        type="button"
        onClick={onToggleFilters}
        className={classnames(
          'btn btn-outline-secondary border-0 p-0 d-flex align-items-center justify-content-center text-neutral-80 shadow-none',
          { active: filtersVisible },
        )}
        style={{
 width: '36px', height: '36px', fontSize: '20px', lineHeight: 1,
}}
      >
        <i className="fa-regular fa-sliders" />
      </button>

      {filtersVisible && (
        <div className="d-flex gap-2 mt-2 w-100">
          {isMedia && (
            <RadioFilterChip
              label="Role"
              title="Select Role"
              value={activeFilters.role}
              options={ROLE_OPTIONS}
              onChange={(v) => updateFilter('role', v)}
            />
          )}
          <RadioFilterChip
            label="Status"
            title="Select Status"
            value={activeFilters.status}
            options={STATUS_OPTIONS}
            onChange={(v) => updateFilter('status', v)}
          />
          <PostsFilter
            label={isMedia ? 'Posts' : 'Opportunity'}
            value={activeFilters.posts}
            posts={postOptions}
            loading={optionsLoading}
            onOpen={loadPostOptions}
            onChange={(v) => updateFilter('posts', v)}
          />
        </div>
      )}

      {hasActiveFilters && (
        <div className="d-flex align-items-start w-100 mt-2 gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2 flex-grow-1">
            {activeRole && (
              <button
                type="button"
                className="btn btn-sm rounded-pill bg-accent-blue-10 text-accent-blue-80 border border-accent-blue-20 d-inline-flex align-items-center gap-2 px-3 py-1 paragraph-sm fw-bold shadow-none filter-tag-pill"
                onClick={() => updateFilter('role', null)}
              >
                {ROLE_OPTIONS.find((o) => o.value === activeRole)?.label.replace(' only', '')}
                <i className="fa-solid fa-xmark text-neutral-60" style={{ fontSize: 12 }} />
              </button>
            )}
            {activeFilters.status && (
              <button
                type="button"
                className="btn btn-sm rounded-pill bg-accent-blue-10 text-accent-blue-80 border border-accent-blue-20 d-inline-flex align-items-center gap-2 px-3 py-1 paragraph-sm fw-bold shadow-none filter-tag-pill"
                onClick={() => updateFilter('status', null)}
              >
                {STATUS_OPTIONS.find((o) => o.value === activeFilters.status)?.label.replace(' only', '')}
                <i className="fa-solid fa-xmark text-neutral-60" style={{ fontSize: 12 }} />
              </button>
            )}
            {activeFilters.posts && (
              <button
                type="button"
                className="btn btn-sm rounded-pill bg-accent-blue-10 text-accent-blue-80 border border-accent-blue-20 d-inline-flex align-items-center gap-2 px-3 py-1 paragraph-sm fw-bold shadow-none filter-tag-pill"
                onClick={() => updateFilter('posts', null)}
                style={{ maxWidth: 200 }}
                // The chip truncates at 200px; hovering reveals the whole request title.
                title={
                  postOptions.find((o) => String(o.value) === String(activeFilters.posts))?.label
                  ?? String(activeFilters.posts)
                }
              >
                <span className="text-truncate">
                  {postOptions.find((o) => String(o.value) === String(activeFilters.posts))?.label
                    ?? activeFilters.posts}
                </span>
                <i className="fa-solid fa-xmark text-neutral-60 flex-shrink-0" style={{ fontSize: 12 }} />
              </button>
            )}
          </div>

          <button
            type="button"
            className="btn btn-sm border-0 bg-transparent text-primary-60 paragraph-sm fw-medium p-0 clear-all-link flex-shrink-0"
            onClick={clearAllFilters}
          >
            Clear All
          </button>
        </div>
      )}
    </>
  );
}

const filterOptionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

RadioFilterChip.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(filterOptionShape).isRequired,
  onChange: PropTypes.func.isRequired,
};

ConversationsFilter.propTypes = {
  activeFilters: PropTypes.shape({
    role: PropTypes.string,
    status: PropTypes.string,
    posts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  isMedia: PropTypes.bool,
};

export default ConversationsFilter;
